import styled from "styled-components";
import { useAnalyticsGroupWidgetContext } from "../../../helpers/hooks/useAnalyticsGroupWidgetContext";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import { useFetchAnalyticsWidgetData } from "../../../helpers/hooks/useFetchAnalyticsWidgetData";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/store";
import { useParams } from "react-router-dom";
import { useMeasure } from "react-use";
import AreaChart from "./areaChart";
import WidgetLoadError from "../../widgetLoadError";
import WidgetLoading from "../../widgetLoading";
import WidgetEmptyData from "../../widgetEmptyData";
interface WidgetData {
  data: any[];
  config: {
    xAxis: {
      label: string;
    };
    yAxis: {
      label: string;
    };
    values: {
      accessor: string;
      label: string;
      color: string;
      axisPosition: string;
      value_formatter: string;
    }[];
    category: string;
  };
}

const SalesTrendsForecastWidget = () => {
  const routeMatch = useParams();
  const { widgetMeta } = useAnalyticsGroupWidgetContext();
  const [selectedTabUrl, setSelectedTabUrl] = useState<string | null>(null);
  const [ref, dims] = useMeasure<HTMLDivElement>();

  const getTabTitle = (url: string) => {
    if (url.includes("hourly")) {
      return "Hourly";
    }
    if (url.includes("daily")) {
      return "Daily";
    }
    return url.split("/").pop();
  };

  const { filters } = useSelector((state: RootState) => state);
  const { dates } = useSelector((state: RootState) => state.filters);
  const { locations } = useSelector((state: RootState) => state.filters);


  // Calculate date difference to conditionally show hourly tab
  const getDateDifference = (startDate: string, endDate: string): number => {
    const [startYear, startMonth, startDay] = startDate.split("-");
    const [endYear, endMonth, endDay] = endDate.split("-");
    const start = new Date(
      parseInt(startYear),
      parseInt(startMonth) - 1,
      parseInt(startDay)
    );
    const end = new Date(
      parseInt(endYear),
      parseInt(endMonth) - 1,
      parseInt(endDay)
    );
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isHourlyTabVisible =
    dates && dates.length === 2
      ? getDateDifference(dates[0], dates[1]) < 14
      : false;

  const widgetTabs = (widgetMeta?.sub_widgets ?? [])
    .map((tab) => ({
      id: tab.url,
      title: getTabTitle(tab.url),
    }))
    .filter((tab) => {
      if (tab.id.includes("hourly") && !isHourlyTabVisible) {
        return false;
      }
      return true;
    });

  // Ensure we have a selected tab URL, fallback to first tab
  const activeTabUrl =
    selectedTabUrl || (widgetTabs.length > 0 ? widgetTabs[0].id : null);

  // Check if exactly one location is selected
  const hasExactlyOneLocation = locations && locations.length === 1;
  const shouldFetchData = hasExactlyOneLocation;

  const {
    data,
    isInputRequired,
    overrideMessage,
    toolTipMessage,
    filterUsedByWidget,
    isValidating,
    error,
    reloadData,
  } = useFetchAnalyticsWidgetData({
    dashboard: routeMatch?.dashboard || "",
    dashboardWidget: routeMatch?.widget || "",
    widgetUrl: shouldFetchData ? (activeTabUrl || "") : "",
    filters: filters,
  });

  const widgetData: WidgetData = useMemo(() => {
    let chartConfig: WidgetData["config"] = {
      xAxis: { label: "" },
      yAxis: { label: "" },
      values: [],
      category: "",
    };
    let chartData: WidgetData["data"] = [];
    if (data?.widget) {
      chartConfig = data.widget.config;
      chartData = data.widget.data;
    }
    return { data: chartData, config: chartConfig };
  }, [data]);

  useEffect(() => {
    if (widgetTabs.length > 0 && !selectedTabUrl) {
      setSelectedTabUrl(widgetTabs[0].id);
    }
  }, [widgetTabs, selectedTabUrl]);

  return (
    <StyledSalesTrendsForecastWidget ref={ref}>
      <div className="widget-header">
        <div className="widget-title">{widgetMeta?.title || "Sales Trend"}</div>
        <div className="widget-actions">
          <div className="widget-tabs">
            {widgetTabs.map((tab) => (
              <div
                key={tab.id}
                className={`widget-tab ${
                  activeTabUrl === tab.id ? "selected" : ""
                }`}
                onClick={() => setSelectedTabUrl(tab.id)}>
                {tab.title}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="widget-content">
        {!shouldFetchData ? (
          <div className="location-feedback">
            <div className="feedback-message">
              {!locations || locations.length === 0 ? (
                <>
                  <div className="feedback-icon">📍</div>
                  <div className="feedback-title">No Location Selected</div>
                  <div className="feedback-text">
                    Please select a location to view sales trends and forecast
                    data.
                  </div>
                </>
              ) : (
                <>
                  <div className="feedback-title">
                    Multiple Locations Selected
                  </div>
                  <div className="feedback-text">
                    Please select exactly one location to view detailed sales
                    trends and forecast.
                  </div>
                </>
              )}
            </div>
          </div>
        ) : isValidating ? (
          <WidgetLoading />
        ) : error ? (
          <WidgetLoadError />
        ) : widgetData.data.length > 0 && dims.height > 0 && dims.width > 0 ? (
          <AreaChart
            data={widgetData.data}
            config={widgetData.config}
            height={dims.height - 80}
            width={dims.width - 40}
          />
        ) : (
          <WidgetEmptyData height={dims.height - 80} width={dims.width - 40} />
        )}
      </div>
    </StyledSalesTrendsForecastWidget>
  );
};

export default SalesTrendsForecastWidget;

const StyledSalesTrendsForecastWidget = styled.div`
  border: 1px solid ${({ theme }) => theme.widget_group.border};
  border-radius: 20px;
  background-color: ${({ theme }) => theme.widget.background};
  filter: drop-shadow(5px 5px 10px rgba(0, 0, 0, 0.05));
  &.outline {
    border-radius: 20px;
    border: 10px solid rgb(172, 213, 202);
  }
  width: 100%;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;

  .widget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-shrink: 0;

    .widget-title {
      font-size: 1.3rem;
      text-decoration: underline;
    }

    .widget-actions {
      display: flex;
      align-items: center;
      gap: 12px;

      .widget-tabs {
        display: flex;
        border-radius: 6px;
        padding: 2px;

        .widget-tab {
          cursor: pointer;
          min-width: 80px;
          padding: 10px;
          margin: 5px;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 500;
          line-height: 18px;
          text-align: center;
          background: rgb(246, 246, 246);
          border-radius: 20px;
          color: rgb(54, 53, 55);
          transition: all 0.2s;

          &.selected {
            background-color: #1f2937;
            color: white;
          }

          &:hover:not(.selected) {
            background-color: #e5e7eb;
          }
        }
      }
    }
  }

  .widget-content {
    flex: 1;
    min-height: 0;
    width: 100%;
    height: 100%;
    position: relative;

    .location-feedback {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;

      .feedback-message {
        text-align: center;
        max-width: 400px;
        padding: 40px 20px;

        .feedback-icon {
          font-size: 3rem;
          margin-bottom: 16px;
        }

        .feedback-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: ${({ theme }) => theme.text || '#1f2937'};
          margin-bottom: 12px;
        }

        .feedback-text {
          font-size: 0.95rem;
          color: ${({ theme }) => theme.textSecondary || '#6b7280'};
          line-height: 1.5;
        }
      }
    }
  }
`;
